import {CLStore} from 'common/src/redux/store';
import {creativesApi, ParamsForDTCreative} from 'common/src/modules/creatives/creatives.api';
import {Creative} from 'common/src/modules/creatives/creatives.types';
import {TemplateType} from 'common/src/modules/creativeTemplates/creativeTemplates.types';
import getCampaignTemplateTypes from './getCampaignTemplateTypes';
import {CAMPAIGN_NODE_ID} from 'common/src/components/CLCardsMap/CLCardsMap.config';

const TEMPLATE_TYPE_LABELS: Record<number, string> = {
  [TemplateType.DisplayAds]: 'DisplayAds',
  [TemplateType.VideoAds]: 'VideoAds',
  [TemplateType.ImageAds]: 'ImageAds',
  [TemplateType.ExternalAds]: 'ExternalAds',
  [TemplateType.InteractiveVideoAds]: 'InteractiveVideoAds',
  [TemplateType.InBannerVideoAds]: 'InBannerVideoAds',
};

export type GetCreativeForCampaignResult =
  | {found: true; creative: Creative}
  | {found: false; errorMessage: string};

/**
 * Fetches creatives compatible with the campaign via getForDecisionTree (single API call)
 * and returns the creative if found, or an error message if not available.
 */
async function getCreativeForCampaign(
  store: CLStore,
  creativeId: number,
  cardId: number,
  isFallback: boolean,
): Promise<GetCreativeForCampaignResult> {
  const campaign = store.getState().campaignPage.campaignTree.campaign;

  if (!campaign) {
    return {found: false, errorMessage: 'No campaign found.'};
  }

  const templateTypes = getCampaignTemplateTypes(campaign.type);
  const params: ParamsForDTCreative = {
    campaignId: campaign.id,
    getStyleInstructions: true,
    templateType: templateTypes.length > 0 ? templateTypes : undefined,
    ...(cardId > CAMPAIGN_NODE_ID && {attachedToNodeId: cardId}),
    ...(isFallback && {isForFallbackCreative: true}),
  };

  const compatibleCreatives = await creativesApi.getForDecisionTree(params);
  const creative = compatibleCreatives.find((c) => c.id === creativeId);

  if (creative) {
    return {found: true, creative};
  }

  const expectedTypesStr =
    templateTypes.length > 0
      ? templateTypes.map((t) => TEMPLATE_TYPE_LABELS[t] ?? String(t)).join(', ')
      : 'none';
  const fallbackNote = isFallback ? ', and must not use products/location/content/weather' : '';

  return {
    found: false,
    errorMessage: `Creative with ID ${creativeId} is not available for this campaign. It either doesn't exist in the library or is not compatible with this campaign's requirements (expected template types: ${expectedTypesStr}${fallbackNote}).`,
  };
}

export default getCreativeForCampaign;
